import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IStepperContext, StepperContext, StyledButton } from '../../ui';
import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/20/solid';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IPersonPickerProps {}

const possiblePersonCounts = Array.from({ length: 4 }, (_, i) => i + 1);

export function PersonPicker(props: IPersonPickerProps) {
  const { t } = useTranslation();
  const methods = useFormContext();
  const stepperContext = useContext<IStepperContext>(StepperContext);

  const [rooms, setRooms] = useState<number[]>([1]);

  const personWatch: number = methods.watch('personCount');
  const roomWatch: number = methods.watch('roomCount');

  useEffect(() => {
    if (personWatch === 1) {
      methods.setValue('roomCount', 1);
      setRooms([1]);
    } else if (personWatch === 2) {
      if (roomWatch > 2) {
        methods.setValue('roomCount', 1);
      }

      setRooms([1, 2]);
    } else {
      const min = Math.max(1, Math.round(personWatch / 2));

      const newRooms = Array.from(
        { length: personWatch },
        (_, i) => i + 1
      ).filter((ct) => ct >= min);

      if (!newRooms.includes(roomWatch)) {
        methods.setValue('roomCount', min);
      }
      setRooms(newRooms);
    }

    //reset cures -> persons have changes
    methods.setValue('cureList', []);
  }, [personWatch]);

  useEffect(() => {
    methods.setValue('roomList', []);
    methods.setValue('from', null);
    methods.setValue('to', null);
  }, [roomWatch]);

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="container px-2 mb-12 text-brand-cashmere-900">
        <div
          id="teaser"
          className={classNames(
            'my-4',
            'flex flex-col items-center text-center space-y-2',
            '*:max-w-2xl'
          )}
        >
          <h1 className="text-3xl font-serif">
            {t('reservation.header.title')}
          </h1>
          <h2 className="font-light">{t('reservation.header.subtitle')}</h2>
          {/* <p className="text-gray-700 flex flex-col">
            <span>{t('voucher.header.description')}</span>
            <span className="mt-1">{t('voucher.header.signature')}</span>
          </p> */}
        </div>
      </div>

      <div className="space-y-2 max-w-lg w-full">
        <div className="space-y-2">
          <h2 className="font-medium text-brand-cashmere-800">
            {t('reservation.person-picker.person-count')}
          </h2>

          {personWatch > 2 && (
            <p className="text-sm max-w-lg mb-5">
              {t('reservation.person-picker.high-person.disclaimer')}

              <a
                href="https://anfrage.mandira-ayurveda.at"
                className="underline text-brand-pink flex gap-2 items-center"
                rel="noreferrer noopener"
                target="_blank"
              >
                {t('reservation.person-picker.high-person.enquiry')}
                <ArrowRightIcon className="size-3" />
              </a>
            </p>
          )}

          <div className="flex gap-2">
            {possiblePersonCounts.map((ct) => (
              <StyledButton
                key={ct}
                design={personWatch === ct ? 'primary' : 'default'}
                className="aspect-square"
                type="button"
                onClick={() => methods.setValue('personCount', ct)}
              >
                <span className="w-full h-full flex items-center justify-center">
                  {ct}
                </span>
              </StyledButton>
            ))}
          </div>
        </div>

        <div className={classNames('space-y-2', personWatch === 1 && 'hidden')}>
          <h2 className="font-medium text-brand-cashmere-800">
            {t('reservation.person-picker.room-count')}
          </h2>

          <div className="flex gap-1">
            {rooms.map((ct) => (
              <StyledButton
                key={ct}
                design={roomWatch === ct ? 'primary' : 'default'}
                type="button"
                className="aspect-square"
                onClick={() => methods.setValue('roomCount', ct)}
              >
                <span className="w-full h-full flex items-center justify-center">
                  {ct}
                </span>
              </StyledButton>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:flex items-center justify-center gap-2 bottom-0 right-0 p-4 fixed bg-brand-cashmere-100 w-full">
          <StyledButton
            type="button"
            design="primary"
            className="w-full md:w-48 h-12 md:h-auto"
            pulse={true}
            onClick={() =>
              stepperContext.onNextStep && stepperContext.onNextStep()
            }
          >
            <span className="flex justify-center items-center w-full h-full">
              {t('reservation.forward')}
            </span>
          </StyledButton>
        </div>
      </div>
    </div>
  );
}

export default PersonPicker;
